import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { Crown } from "@phosphor-icons/react";

/**
 * MeetQualifiers — from Day 28 onwards, showcases the current Top-5 of each
 * qualifying country. Public data via `/championship/qualifiers`.
 *
 * Purpose: build anticipation + give qualifiers a moment of celebrity before
 * the Day-31 championship kicks off.
 */
export default function MeetQualifiers() {
  const [data, setData] = useState(null);
  const [day, setDay] = useState(null);

  useEffect(() => {
    api.get("/season/current").then((r) => setDay(r.data?.day)).catch(() => {});
    api.get("/championship/qualifiers").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (day == null || day < 28) return null;
  if (!data?.countries) return null;

  const countries = Object.entries(data.countries);

  return (
    <div className="nk-card p-5" data-testid="meet-qualifiers">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="eyebrow text-nk-gold mb-1">Meet the Qualifiers</p>
          <h3 className="font-display text-xl font-medium tracking-tight">
            10 champions. One continental crown.
          </h3>
        </div>
        <p className="text-xs text-nk-mute">
          {day === 30 ? "Locked in" : `Standings updating live · Day ${day}/32`}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {countries.map(([code, seeds]) => (
          <div
            key={code}
            className="border border-nk-line rounded-sm p-4 bg-nk-bg/40"
            data-testid={`meet-qualifiers-${code.toLowerCase()}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <CountryFlag country={code} size={16} />
              <span className="font-medium text-sm">
                {code === "GH" ? "Ghana" : "Nigeria"}
              </span>
              <span className="ml-auto text-xs text-nk-mute font-mono">
                Top {seeds.length}
              </span>
            </div>
            {seeds.length === 0 ? (
              <p className="text-xs text-nk-mute italic py-2">
                No qualifying players yet — first 5 to accrue Season Score claim these spots.
              </p>
            ) : (
              <ol className="space-y-2">
                {seeds.map((s) => (
                  <li key={s.user_id} className="flex items-center gap-2.5 text-sm">
                    <span className={`w-4 text-center font-mono font-bold text-xs ${
                      s.seed === 1 ? "text-nk-gold" : "text-nk-mute"
                    }`}>
                      {s.seed === 1 ? <Crown size={12} weight="fill" /> : `#${s.seed}`}
                    </span>
                    <Link
                      to={`/u/${s.username}`}
                      className="flex items-center gap-2 hover:text-nk-accent transition-colors flex-1 min-w-0"
                    >
                      <AvatarIcon id={s.avatar} size={20} />
                      <span className="font-medium truncate">{s.username}</span>
                    </Link>
                    <span className="text-nk-accent font-mono text-xs">
                      {s.season_score.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
